import { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../config/firebase';
import { BeeBaseAlert } from '../../types';

interface Filter {
  disease?: string;
  startDate?: string; // ISO
  endDate?: string;   // ISO
}

// Simple geocoding function for UK regions and locations
function getCoordinatesForLocation(region: string | null, location: string | null): { lat?: number; lng?: number } {
  const locationStr = `${region || ''} ${location || ''}`.toLowerCase();
  
  // UK region coordinates (approximate centers)
  const regionCoords: { [key: string]: { lat: number; lng: number } } = {
    'devon': { lat: 50.7184, lng: -3.5339 },
    'exeter': { lat: 50.7184, lng: -3.5339 },
    'somerset': { lat: 51.1045, lng: -3.0270 },
    'taunton': { lat: 51.0157, lng: -3.1026 },
    'cornwall': { lat: 50.2632, lng: -5.0510 },
    'truro': { lat: 50.2632, lng: -5.0510 },
    'yorkshire': { lat: 53.9921, lng: -1.5418 },
    'harrogate': { lat: 53.9921, lng: -1.5418 },
    'kent': { lat: 51.2802, lng: 1.0789 },
    'canterbury': { lat: 51.2802, lng: 1.0789 },
    'sussex': { lat: 50.8436, lng: -0.1415 },
    'surrey': { lat: 51.3148, lng: -0.5600 },
    'hampshire': { lat: 51.0577, lng: -1.3080 },
    'dorset': { lat: 50.8429, lng: -2.2999 },
    'bristol': { lat: 51.4545, lng: -2.5879 },
    'gloucestershire': { lat: 51.8330, lng: -2.2156 },
    'wiltshire': { lat: 51.3500, lng: -1.9930 },
    'oxfordshire': { lat: 51.7612, lng: -1.2577 },
    'berkshire': { lat: 51.4584, lng: -1.0210 },
    'buckinghamshire': { lat: 51.8169, lng: -0.8085 },
    'hertfordshire': { lat: 51.8000, lng: -0.2400 },
    'essex': { lat: 51.7341, lng: 0.4692 },
    'norfolk': { lat: 52.6309, lng: 1.2974 },
    'suffolk': { lat: 52.1872, lng: 0.9708 },
    'cambridgeshire': { lat: 52.2053, lng: 0.1218 },
    'bedfordshire': { lat: 52.0076, lng: -0.4613 },
    'northamptonshire': { lat: 52.2405, lng: -0.9027 },
    'warwickshire': { lat: 52.2819, lng: -1.5336 },
    'leicestershire': { lat: 52.7342, lng: -1.1136 },
    'nottinghamshire': { lat: 53.0878, lng: -1.0659 },
    'derbyshire': { lat: 53.1362, lng: -1.4897 },
    'staffordshire': { lat: 52.8551, lng: -2.0596 },
    'shropshire': { lat: 52.5677, lng: -2.7907 },
    'herefordshire': { lat: 52.0567, lng: -2.7150 },
    'worcestershire': { lat: 52.1923, lng: -2.2162 },
    'lincolnshire': { lat: 53.2307, lng: -0.5406 },
    'rutland': { lat: 52.6581, lng: -0.6253 },
    'london': { lat: 51.5074, lng: -0.1278 },
    'greater london': { lat: 51.5074, lng: -0.1278 },
    'scotland': { lat: 55.9533, lng: -3.1883 },
    'edinburgh': { lat: 55.9533, lng: -3.1883 },
    'glasgow': { lat: 55.8642, lng: -4.2518 },
    'wales': { lat: 52.1307, lng: -3.7837 },
    'cardiff': { lat: 51.4816, lng: -3.1791 },
    'swansea': { lat: 51.6214, lng: -3.9436 },
    'northern ireland': { lat: 54.7877, lng: -6.4923 },
    'belfast': { lat: 54.5973, lng: -5.9301 }
  };
  
  // Try to find coordinates for the location
  for (const [place, coords] of Object.entries(regionCoords)) {
    if (locationStr.includes(place)) {
      return coords;
    }
  }
  
  // Default to center of UK if no match found
  return {};
}

export function useBeeBaseAlerts(filter: Filter) {
  const [alerts, setAlerts] = useState<BeeBaseAlert[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchAlerts = async () => {
      setLoading(true);
      try {
        let ref = collection(db, 'beebase_alerts');
        const q = query(ref); // expand later with where clauses if filtering server-side
        const snap = await getDocs(q);
        let all: BeeBaseAlert[] = [];
        snap.forEach(doc => all.push({ id: doc.id, ...doc.data() } as BeeBaseAlert));

        // Client-side filtering
        if (filter.disease) {
          all = all.filter(a => a.disease?.toLowerCase() === filter.disease?.toLowerCase());
        }
        if (filter.startDate && filter.startDate !== '') {
          all = all.filter(a => a.date && new Date(a.date) >= new Date(filter.startDate!));
        }
        if (filter.endDate && filter.endDate !== '') {
          all = all.filter(a => a.date && new Date(a.date) <= new Date(filter.endDate!));
        }

        // Add simple geocoding for alerts that don't have coordinates
        all = all.map(alert => {
          if (alert.lat === undefined || alert.lng === undefined) {
            const coords = getCoordinatesForLocation(alert.region, alert.location);
            return { ...alert, ...coords };
          }
          return alert;
        });

        // Only include alerts that now have coordinates (after geocoding)
        all = all.filter(a => a.lat !== undefined && a.lng !== undefined);

        setAlerts(all);
      } catch (error) {
        console.error('Error fetching BeeBase alerts:', error);
        setAlerts([]);
      }
      setLoading(false);
    };
    
    fetchAlerts();
  }, [filter.disease, filter.startDate, filter.endDate]);

  return { alerts, loading, alertCount: alerts.length };
}
